import { ColorType } from 'lightweight-charts'

// Formatter for Tooltips and Axis — shows date + time
export const formatTime = (time: number) => {
    const date = new Date(time * 1000)
    return (
        date.toLocaleDateString('en-IN', {
            timeZone: 'Asia/Kolkata',
            day: '2-digit',
            month: 'short',
        }) +
        ' ' +
        date.toLocaleTimeString('en-IN', {
            timeZone: 'Asia/Kolkata',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        })
    )
}

export const CHART_THEME = {
    localization: {
        timeFormatter: formatTime,
    },
    layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: '#64748b', // Slate-500
        fontFamily: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontSize: 11,
    },
    grid: {
        vertLines: { color: 'rgba(226, 232, 240, 0.7)' },
        horzLines: { color: 'rgba(226, 232, 240, 0.7)' },
    },
    crosshair: {
        vertLine: {
            color: '#64748b',
            width: 1,
            style: 3, // Dashed
            labelBackgroundColor: '#334155',
        },
        horzLine: {
            color: '#64748b',
            width: 1,
            style: 3,
            labelBackgroundColor: '#334155',
        },
    },
    timeScale: {
        borderColor: 'rgba(226, 232, 240, 0.8)',
        timeVisible: true,
        secondsVisible: false,
        borderVisible: true,
        rightOffset: 12,
        barSpacing: 6,
        minBarSpacing: 2,
        fixLeftEdge: false,
        fixRightEdge: false,
        tickMarkFormatter: (time: number) => {
            const date = new Date(time * 1000)
            const timeStr = date.toLocaleTimeString('en-IN', {
                timeZone: 'Asia/Kolkata',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
            })
            return timeStr
        },
    },
    rightPriceScale: {
        borderColor: 'rgba(226, 232, 240, 0.8)',
        borderVisible: true,
        scaleMargins: {
            top: 0.05,
            bottom: 0.45,
        },
    },
    leftPriceScale: {
        borderColor: 'rgba(226, 232, 240, 0.8)',
        borderVisible: true,
        scaleMargins: {
            top: 0.50,
            bottom: 0.05,
        },
    },
}

export const CHART_THEME_DARK = {
    localization: {
        timeFormatter: formatTime,
    },
    layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: '#94a3b8', // Slate-400
        fontFamily: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontSize: 11,
    },
    grid: {
        vertLines: { color: 'rgba(51, 65, 85, 0.35)' },
        horzLines: { color: 'rgba(51, 65, 85, 0.35)' },
    },
    crosshair: {
        vertLine: {
            color: '#94a3b8',
            width: 1,
            style: 3,
            labelBackgroundColor: '#1e293b',
        },
        horzLine: {
            color: '#94a3b8',
            width: 1,
            style: 3,
            labelBackgroundColor: '#1e293b',
        },
    },
    timeScale: {
        borderColor: 'rgba(51, 65, 85, 0.5)',
        timeVisible: true,
        secondsVisible: false,
        borderVisible: true,
        rightOffset: 12,
        barSpacing: 6,
        minBarSpacing: 2,
        fixLeftEdge: false,
        fixRightEdge: false,
        tickMarkFormatter: (time: number) => {
            const date = new Date(time * 1000)
            const timeStr = date.toLocaleTimeString('en-IN', {
                timeZone: 'Asia/Kolkata',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
            })
            return timeStr
        },
    },
    rightPriceScale: {
        borderColor: 'rgba(51, 65, 85, 0.5)',
        borderVisible: true,
        scaleMargins: {
            top: 0.05,
            bottom: 0.45,
        },
    },
    leftPriceScale: {
        borderColor: 'rgba(51, 65, 85, 0.5)',
        borderVisible: true,
        scaleMargins: {
            top: 0.50,
            bottom: 0.05,
        },
    },
}

export const CHART_COLORS = {
    // Primary Colors
    PRIMARY: '#4f46e5', // Indigo-600
    SECONDARY: '#8b5cf6', // Violet-500
    SUCCESS: '#10b981', // Emerald-500
    DANGER: '#f43f5e', // Rose-500
    WARNING: '#f59e0b', // Amber-500
    INFO: '#3b82f6', // Blue-500

    // Backgrounds (Gradient Starts)
    AREA_TOP_DEFAULT: '#4f46e5',
    AREA_BOTTOM_DEFAULT: 'rgba(79, 70, 229, 0.28)',

    // Specialized
    SUPPORT: '#10b981',
    RESISTANCE: '#f43f5e',
    NEUTRAL: '#64748b',

    // Text
    TEXT_MUTED: '#94a3b8',

    // Specific
    TEXT: {
        POSITIVE: '#10b981',
        NEGATIVE: '#f43f5e',
    },
}

/**
 * Robustly parse various timestamp formats into Unix Seconds (Lightweight Charts strict requirement)
 */
export const parseToUnixSeconds = (timestamp: string | number): number | null => {
    if (timestamp === undefined || timestamp === null) return null

    let timeVal: number

    if (typeof timestamp === 'number') {
        // If > 1e11 (100 billion), it's milliseconds (Year 1973+).
        // 13 digits (trillions) = current dates in MS.
        // 10 digits = Seconds.
        timeVal = timestamp > 1e11 ? timestamp / 1000 : timestamp
    } else {
        // String Parsing
        // Handle "DD-MM-YYYY HH:mm:ss" custom format if needed
        if (typeof timestamp === 'string' && timestamp.includes('-') && timestamp.includes(':')) {
            // Try standard constructor first (handles ISO "YYYY-MM-DDTHH:mm:ss")
            let d = new Date(timestamp)

            // If Invalid, try manual DD-MM-YYYY parse
            if (Number.isNaN(d.getTime())) {
                const [datePart, timePart] = timestamp.split(' ')
                if (datePart && timePart) {
                    const parts = datePart.split('-')
                    // Check if DD-MM-YYYY
                    if (parts.length === 3 && parts[0] && parts[2] && parts[0].length === 2 && parts[2].length === 4) {
                        // Swap to YYYY-MM-DD for standard parsing
                        d = new Date(`${parts[2]}-${parts[1]}-${parts[0]}T${timePart}`)
                    }
                }
            }

            if (!Number.isNaN(d.getTime())) {
                timeVal = d.getTime() / 1000
            } else return null
        } else {
            const d = new Date(timestamp)
            if (Number.isNaN(d.getTime())) return null
            timeVal = d.getTime() / 1000
        }
    }

    if (Number.isNaN(timeVal) || !Number.isFinite(timeVal)) return null
    return Math.floor(timeVal)
}
